import type { Credential, Region, Track } from './contracts';

export const PASS_MARK_DEFAULT = 80;
export const VALIDITY_MONTHS = 12;
export const EXPIRING_DAYS = 60;
export const QUALIFICATION_MODULE = 'M-07';

export type ModuleMeta = { id: string; track: Track; region: Region | null; operator_slug: string | null };

export function passMarkFor(
  item: { id: string; track: Track },
  tenant: { operator_pass_mark: number | null },
): number {
  if (item.id === QUALIFICATION_MODULE) return 100;
  if (item.track === 'operator' && tenant.operator_pass_mark !== null) return tenant.operator_pass_mark;
  return PASS_MARK_DEFAULT;
}

export function isPass(score: number, total: number, passMark: number): boolean {
  if (total <= 0) return false;
  return score * 100 >= passMark * total;
}

export function foundationEarned(
  passed: Set<string>,
  modules: ModuleMeta[],
  operatorSlug: string | null,
): boolean {
  const core = modules.filter((m) => m.track === 'core');
  if (core.length === 0 || !core.every((m) => passed.has(m.id))) return false;
  if (operatorSlug === null) return true;
  const op = modules.filter((m) => m.track === 'operator' && m.operator_slug === operatorSlug);
  return op.every((m) => passed.has(m.id));
}

export function specialistRegionsEarned(passed: Set<string>, modules: ModuleMeta[]): Region[] {
  const out: Region[] = [];
  for (const m of modules) {
    if (m.track === 'regional' && m.region && passed.has(m.id) && !out.includes(m.region)) out.push(m.region);
  }
  return out;
}

export function credentialStatus(validUntil: string, now: Date): 'current' | 'expiring' | 'expired' {
  const until = new Date(validUntil).getTime();
  const t = now.getTime();
  if (until <= t) return 'expired';
  if (until - t <= EXPIRING_DAYS * 864e5) return 'expiring';
  return 'current';
}

export function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString();
}

/**
 * How a Specialist reads before it can be earned: the policy below, as the one short line the
 * qualification tiers carry on module cards, module pages and Record.
 */
export const SPECIALIST_OPENS = 'Opens when Foundation is current';

export const SPECIALIST_POLICY =
  'Foundation must be current when a Specialist is issued or renewed. Each existing credential keeps its own validity period unless revoked.';

type CredentialState = { tier: string; status: string; valid_until: string };

/** Current Foundation is an award/renewal prerequisite; holding an existing Specialist is independent. */
export function credentialEligibility(row: CredentialState, now: Date, foundation?: CredentialState) {
  const status: Credential['status'] =
    row.status === 'revoked' ? 'revoked' : credentialStatus(row.valid_until, now);
  const foundationCurrent =
    foundation?.tier === 'foundation' &&
    foundation.status !== 'revoked' &&
    credentialStatus(foundation.valid_until, now) !== 'expired';
  return {
    status,
    supports_specialist: row.tier === 'foundation' && status !== 'revoked' && status !== 'expired',
    can_renew: status !== 'revoked' && (row.tier === 'foundation' || foundationCurrent),
  };
}

export function credentialLabel(tier: string, region: string | null): string {
  const named = region ? region.charAt(0).toUpperCase() + region.slice(1) : null;
  return tier === 'foundation' ? 'Foundation' : named ? `${named} Specialist` : 'Specialist';
}

/** The same recorded criteria appear on the certificate, in metadata and in the public register. */
export function credentialCriteria(credential: Pick<Credential, 'tier' | 'policy'>): string[] {
  const issued = credential.policy?.issued;
  const renewed = credential.policy?.renewed;
  const lines = issued
    ? issued.modules.map((m) => `${m.title}: pass mark ${m.pass_mark} percent.`)
    : ['Detailed issue-time assessment criteria were not recorded for this historical credential.'];
  if (issued?.foundation) lines.unshift('Foundation was current when this Specialist was issued.');
  if (renewed)
    lines.push(
      `Renewed on ${renewed.at.slice(0, 10)}: renewal assessment pass mark ${renewed.pass_mark} percent.${renewed.update ? ` Update: ${renewed.update.title} (${renewed.update.id}).` : ''}`,
    );
  if (credential.tier === 'specialist') lines.push(SPECIALIST_POLICY);
  return lines;
}
